/* eslint-disable max-lines-per-function */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { CommonGlobalService, CommonSectorService, CommonSiteService, CommonUtilsService, dayjs, FORMAT_DATE_API, Option, RootState, Sector, Site, usePersistantState, UserUmanforge, UUID, WeekDays } from '@umanforge/common';

import { EditBoxIcon } from '@common_components/icons/editBox.icon';
import { PlusIcon } from '@common_components/icons/plus.icon';

import BaseModal from '@common_components/modals/baseModal.component';
import ConfirmationModal from '@common_components/modals/confirmationModal.component';
import BottomModal from '@common_components/modals/bottomModal.component';
import Tabs, { TabItem } from '@common_components/buttons/Tabs.component';

import HolidayManager from 'components/modals/eventManagers/holidayManager.component';
import EventManagerMainSettings from 'components/modals/eventManagers/eventManagerMainSettings.component';
import type { ChartSection, ChartSectionListHandle } from 'components/inputs/chartSectionList.interfaces';
import EventManagerSafetyTab from 'components/modals/eventManagers/eventManagerSafetyTab.component';
import EventManagerParticipantOrderTab from 'components/modals/eventManagers/eventManagerParticipantOrderTab.component';

import { TableCreation } from 'interfaces/types.interface';
import { ThemeService } from 'services/themes.service';
import { EventManager } from 'interfaces/eventManager.interface';
import { Theme } from 'interfaces/theme.interface';

import { EventManagerService } from 'services/eventManagers.service';
import { NewUserPayload, UsersService } from 'services/users.service';
import { KEY } from 'constants/local_storage_key.constant';
import { GlobalSettingsService } from 'services/globalSettings.service';
import { GlobalSetting } from 'interfaces/globalSetting.interface';
import { DEFAULT_BACKLOG } from 'constants/eventManager.constant';
import Holidays from 'date-holidays';
import { HolidayLocation, PutBodyHolidayLocation } from 'interfaces/holidayLocation.interface';
import { ClosedDay, PostClosedDayToEventManager } from 'interfaces/closedDays.interface';
import { HolidayLocationService } from 'services/holidayLocation.service';
import { KEYFRAME } from 'constants/setting.constants';
import { ChartSectionService } from 'services/chartSection.service';
import { ChartUnitService } from 'services/chartUnit.service';
import type { Dispatch } from '@reduxjs/toolkit';
import { ChartUnitApi } from 'interfaces/chartUnit.interface';
import type { GroupedOption } from '@common_components/inputs/selects/selectInput.component';
import EventManagerVisualisationSetting from './eventManagerVisualisationSetting.component';
import { ChartOfSectionService } from 'services/chartOfSection.service';

type SectorGroupOption = { label: string; options: Option<Sector>[] };

interface EventManagerModalProps {
  onSubmit: ( table: TableCreation ) => Promise<UUID | null>;
  onDeleteEventManager?: ( tableId: string ) => Promise<boolean>;
  onClose: () => void;
  tableId?: string;
}

const getSelectedSites = ( allSites: Option<Site>[], sitesId: UUID[] ): Option<Site>[] => {
  return allSites.filter( site => { return sitesId.includes( site.object.id ); } );
};

const getSelectedSectors = ( allSectors: SectorGroupOption[], sectorsId: UUID[] ): Option<Sector>[] => {
  return allSectors
    .map( sector => { return sector.options; } )
    .flat()
    .filter( sector => { return sectorsId.includes( sector.object.id ); } );
};

const getSelectedParticipants = ( allParticipants: Option<UserUmanforge>[], participantsId: UUID[] ): Option<UserUmanforge>[] => {
  return allParticipants.filter( participant => { return participantsId.includes( participant.object.id ); } );
};

const getSelectedAdmins = ( allAdmins: Option<UserUmanforge>[], adminsId: UUID[] ): Option<UserUmanforge>[] => {
  return allAdmins.filter( admin => { return adminsId.includes( admin.object.id ); } );
};

const getViewersId = ( participantsId: UUID[], viewersId: UUID[] ) => {
  const cleanedViewerIds = viewersId.filter( viewerId => {
    return !participantsId
      .filter( participantId => { return participantId != DEFAULT_BACKLOG.ID; } )
      .includes( viewerId );
  } );

  return cleanedViewerIds;
};

const getPositionInfos = ( eventManager: EventManager ) => {
  const orderedIdsWithBacklog: string[] = [];
  const orderedParticipants = [ ...eventManager.participants ]
    .filter( item => { return item.id != DEFAULT_BACKLOG.ID; } )
    .sort( ( a, b ) => {
      if ( a.position !== undefined && b.position !== undefined ) {
        return a.position - b.position;
      }
      return 0;
    } );
  const positions = orderedParticipants
    .map( p => { return p.position; } )
    .filter( pos => { return pos !== undefined; } )
    .sort( ( a, b ) => { return a! - b!; } );
  let backlogPosition = positions.length;
  if ( positions.length > 0 ) {
    for ( let i = 0; i < positions.length; i++ ) {
      if ( positions[ i ] !== i ) {
        backlogPosition = i;
        break;
      }
    }
  }
  const maxPosition = positions.length > 0 ? Math.max( ...positions ) : -1;
  const totalPositions = Math.max( maxPosition + 1, backlogPosition + 1 );
  for ( let pos = 0; pos < totalPositions; pos++ ) {
    if ( pos === backlogPosition ) {
      orderedIdsWithBacklog.push( DEFAULT_BACKLOG.ID );
    } else {
      const participantAtPosition = orderedParticipants.find( p => { return p.position === pos; } );
      if ( participantAtPosition ) {
        orderedIdsWithBacklog.push( participantAtPosition.id );
      }
    }
  }
  const orderedIds = orderedParticipants.map( user => { return user.id; } );
  const participantsAsUmanforge = orderedParticipants.map( user => {
    return {
      ...user,
      isLock: false,
      needChangePassword: false,
      isAdminOfUser: false,
      isSuperAdmin: user.is_super_admin || false,
      isDisable: user.disabled || false
    };
  } ) as UserUmanforge[];

  return { orderedIdsWithBacklog, orderedIds, backlogPosition, participantsAsUmanforge };
};

const saveChartSections = async (
  eventManagerId: UUID,
  chartSections: ChartSection[],
  initialChartSections: ChartSection[],
  token: string | null,
  dispatch: Dispatch,
): Promise<{ hasChanges: boolean; idMap: Map<string, UUID> }> => {
  const initialById = new Map( initialChartSections.map( section => { return [ section.id, section ]; } ) );
  const currentIds = new Set( chartSections.map( section => { return section.id; } ) );

  const sectionsToCreate = chartSections.filter( section => { return !initialById.has( section.id ); } );
  const sectionsToDelete = initialChartSections.filter( section => { return !currentIds.has( section.id ); } );
  const sectionsToRename = chartSections.filter( section => {
    const initial = initialById.get( section.id );
    return initial != null && initial.name !== section.name;
  } );

  const idMap = new Map<string, UUID>();
  initialChartSections.forEach( section => {
    idMap.set( section.id, section.id as UUID );
  } );

  let hasChanges = false;

  for ( const section of sectionsToCreate ) {
    const position = chartSections.findIndex( s => { return s.id === section.id; } );
    const createdId = await ChartSectionService.createChartSection( { eventManagerId, name: section.name, position: position >= 0 ? position : 0 }, token, dispatch );
    if ( createdId ) {
      idMap.set( section.id, createdId );
      hasChanges = true;
    }
  }

  for ( const section of sectionsToRename ) {
    const backendId = idMap.get( section.id ) ?? section.id as UUID;
    const success = await ChartSectionService.updateChartSectionName( backendId, { name: section.name }, token, dispatch );
    if ( success ) {
      hasChanges = true;
    }
  }

  for ( const section of sectionsToDelete ) {
    const success = await ChartSectionService.deleteChartSection( section.id as UUID, token, dispatch );
    if ( success ) {
      hasChanges = true;
    }
  }

  if ( chartSections.length > 0 ) {
    const sectionsWithPosition = chartSections.map( ( section, index ) => {
      const backendId = idMap.get( section.id ) ?? section.id as UUID;
      return {
        id: backendId,
        position: index,
      };
    } );

    const success = await ChartSectionService.updateChartSectionPositions( eventManagerId, sectionsWithPosition, token, dispatch );
    if ( success ) {
      hasChanges = true;
    }
  }

  return { hasChanges, idMap };
};

const saveGraphicUnits = async (
  eventManagerId: UUID,
  graphicUnits: string[],
  initialGraphicUnits: string[],
  initialChartUnits: ChartUnitApi[],
  token: string | null,
  dispatch: Dispatch,
): Promise<boolean> => {
  let hasChanges = false;
  const initialValueSet = new Set( initialGraphicUnits );
  const currentValueSet = new Set( graphicUnits );

  const removedValues: string[] = [];
  const unchangedValues: string[] = [];

  for ( const value of initialValueSet ) {
    if ( currentValueSet.has( value ) ) {
      unchangedValues.push( value );
    } else {
      removedValues.push( value );
    }
  }

  const newValues = graphicUnits.filter( value => { return !initialValueSet.has( value ); } );

  const initialByValue = new Map( initialChartUnits.map( unit => { return [ unit.value, unit ]; } ) );

  const renameCount = Math.min( removedValues.length, newValues.length );

  const valuesToRename = removedValues.slice( 0, renameCount );
  const valuesToCreate = newValues.slice( renameCount );
  const valuesToDelete = removedValues.slice( renameCount );

  for ( let index = 0; index < valuesToRename.length; index++ ) {
    const oldValue = valuesToRename[ index ];
    const newValue = newValues[ index ];
    const unit = initialByValue.get( oldValue );
    if ( unit && unit.id ) {
      const success = await ChartUnitService.updateChartUnit( unit.id, { value: newValue }, token, dispatch );
      if ( success ) {
        hasChanges = true;
      }
    }
  }

  for ( const value of valuesToCreate ) {
    const createdId = await ChartUnitService.createChartUnit( { eventManagerId, value }, token, dispatch );
    if ( createdId ) {
      hasChanges = true;
    }
  }

  for ( const value of valuesToDelete ) {
    const unit = initialByValue.get( value );
    if ( unit ) {
      const success = await ChartUnitService.deleteChartUnit( unit.id, token, dispatch );
      if ( success ) {
        hasChanges = true;
      }
    }
  }

  return hasChanges;
};

const mergeThemeOptionsWithEventManagerThemes = (
  options: Option<Theme>[],
  eventManagerActual?: EventManager,
): Option<Theme>[] => {
  if ( !eventManagerActual ) {
    return options;
  }

  const eventManagerThemeOptions = CommonGlobalService.toOptions( eventManagerActual.themes );
  const mergedOptions = [ ...options ];

  eventManagerThemeOptions.forEach( eventManagerTheme => {
    const exists = mergedOptions.some( option => { return option.object.id === eventManagerTheme.object.id; } );
    if ( !exists ) {
      mergedOptions.push( eventManagerTheme );
    }
  } );

  return mergedOptions;
};


function EventManagerModalInner( {
  onDeleteEventManager,
  onClose,
  onSubmit,
  tableId
}: EventManagerModalProps ): React.ReactElement {
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const pages: TabItem[] = [
    { id: 'principal', name: t( 'translation:principal' ) },
    { id: 'closure', name: t( 'common:closure' ) },
    ...( tableId ? [ { id: 'safety_cross', name: t( 'translation:graphique' ) } ] : [] ),
    { id: 'participant_order', name: t( 'translation:participant_order' ) },
    ...( tableId ? [ { id: 'global_visualisation', name: t( 'translation:global_visualisation' ) } ] : [] ),
  ];

  //--------------- useState ---------------\\
  const [ activeTab, setActiveTab ] = useState( pages[ 0 ].id );

  const [ clientId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_CLIENT_ID, undefined );
  const [ siteId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SITE_ID, undefined );
  const [ sectorId ] = usePersistantState<UUID | undefined>( KEY.SELECTED_SECTOR_ID, undefined );
  const [ , setBacklogPositionForTable ] = usePersistantState<string | undefined>( `backlog-position-${tableId}`, undefined );

  const [ eventManagerActual, setEventManagerActual ] = useState<EventManager | undefined>();
  const [ eventManagerName, setEventManagerName ] = useState( '' );

  const [ visuChartsId, setVisuChartsId ] = useState<UUID[]>( [] );
  const [ updatedVisuChartsId, setUpdatedVisuChartsId ] = useState<UUID[]>( [] );

  const [ selectedSites, setSelectedSites ] = useState<Option<Site>[]>( [] );
  const [ sitesOption, setSitesOption ] = useState<Option<Site>[]>( [] );

  const [ selectedSectors, setSelectedSectors ] = useState<Option<Sector>[]>( [] );
  const [ sectorsOption, setSectorsOption ] = useState<SectorGroupOption[]>( [] );

  const [ selectedAdmins, setSelectedAdmins ] = useState<Option<UserUmanforge>[]>( [] );
  const [ adminsOption, setAdminsOption ] = useState<Option<UserUmanforge>[]>( [] );

  const [ selectedParticipants, setSelectedParticipants ] = useState<Option<UserUmanforge>[]>( [] );
  const [ participantsOption, setParticipantsOption ] = useState<Option<UserUmanforge>[]>( [] );

  const [ selectedThemes, setSelectedThemes ] = useState<Option<Theme>[]>( [] );
  const [ themesOption, setThemesOption ] = useState<Option<Theme>[]>( [] );
  const [ safetyCrossThemeErrorId, setSafetyCrossThemeErrorId ] = useState<string | null>( null );
  const [ safetyCrossThemeWarningId, setSafetyCrossThemeWarningId ] = useState<string | null>( null );
  const [ createEventOnError, setCreateEventOnError ] = useState<boolean>( false );
  const [ createEventOnWarning, setCreateEventOnWarning ] = useState<boolean>( false );
  const [ graphicUnitSuffix, setGraphicUnitSuffix ] = useState<string>( '' );
  const [ graphicUnits, setGraphicUnits ] = useState<string[]>( [] );

  const [ orderedParticipantIds, setOrderedParticipantIds ] = useState<string[]>( [] );
  const [ viewerIds, setViewerIds ] = useState<string[]>( [] );
  const [ triggerParticipantUpdate, setTriggerParticipantUpdate ] = useState( false );
  const [ navbarPosition, setNavbarPosition ] = useState( 'auto' );
  const [ daysBeforeArchived, setDaysBeforeArchived ] = useState( '0' );
  const [ numberOfVisibleDays, setNumberOfVisibleDays ] = useState( '5' );
  const [ activeField, setActiveField ] = useState( false );
  const [ backlogName, setBacklogName ] = useState( DEFAULT_BACKLOG.NAME );
  const [ visibleDays, setVisibleDays ] = useState<WeekDays>( {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false,
  } );

  const [ eventManagerHolidays, setEventManagerHolidays ] = useState<HolidayLocation | null>( null );

  const [ usePublicHolidays, setUsePublicHolidays ] = useState( false );
  const [ addedClosedDays, setAddedClosedDays ] = useState<ClosedDay[]>( [] );
  const [ removedClosedDays, setRemovedClosedDays ] = useState<UUID[]>( [] );
  const [ selectedCountry, setSelectedCountry ] = useState<string | null>( null );
  const [ selectedState, setSelectedState ] = useState<string | null>( null );
  const [ selectedRegion, setSelectedRegion ] = useState<string | null>( null );
  const [ selectedHolidays, setSelectedHolidays ] = useState<{ name: string, id: string }[]>( [] );

  const [ participantsList, setParticipantsList ] = useState<UserUmanforge[]>( [] );
  const [ initialOrderedParticipantIds, setInitialOrderedParticipantIds ] = useState<string[]>( [] );
  const [ initialBacklogPosition, setInitialBacklogPosition ] = useState<number>( -1 );
  const [ initialChartSections, setInitialChartSections ] = useState<ChartSection[]>( [] );
  const [ chartSections, setChartSections ] = useState<ChartSection[]>( [] );
  const [ initialGraphicUnits, setInitialGraphicUnits ] = useState<string[]>( [] );
  const [ initialChartUnits, setInitialChartUnits ] = useState<ChartUnitApi[]>( [] );
  const [ hasPendingChartSettings, setHasPendingChartSettings ] = useState( false );

  const [ isFormValid, setIsFormValid ] = useState( false );
  const [ isFormChanged, setIsFormChanged ] = useState( false );

  const [ isFirstRender, setIsFirstRender ] = useState( true );
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState( false );


  const chartSectionListRef = useRef<ChartSectionListHandle>( null );

  const isAdminOfSelectedSectors = React.useMemo( () => {
    if ( !me ) { return false; }
    if ( me.isSuperAdmin ) { return true; }
    const selectedIds = selectedSectors.map( s => { return s.object.id; } );
    if ( !me.clients ) { return false; }
    for ( const client of me.clients ) {
      if ( client.sites ) {
        for ( const site of client.sites ) {
          if ( site.sectors ) {
            for ( const sector of site.sectors ) {
              if ( sector.isAdmin && selectedIds.includes( sector.id ) ) {
                return true;
              }
            }
          }
        }
      }
    }
    return false;
  }, [ me, selectedSectors ] );


  //--------------- useEffect ---------------\\
  useEffect( () => {
    if ( isFirstRender && clientId && siteId && sectorId ) {
      Promise.all( [
        updateGlobalSettings(),
        init()
      ] )
        .then( () => {  setIsFirstRender( false ); } );
    }

  }, [ isFirstRender, clientId, siteId, sectorId, tableId ] );

  const [ usersCanCreateFromBoard, setUsersCanCreateFromBoard ] = useState<boolean>( false );
  const [ isUserCreationView, setIsUserCreationView ] = useState<boolean>( false );
  const [ newUserFirstName, setNewUserFirstName ] = useState<string>( '' );
  const [ newUserLastName, setNewUserLastName ] = useState<string>( '' );
  const [ newUserEmail, setNewUserEmail ] = useState<string>( '' );

  const chartUnitOptions: GroupedOption[] = [
    {
      options: initialChartUnits.map( unit => {
        return {
          id: unit.id,
          label: unit.value,
        };
      } ),
    },
  ];

  const managerOptions: GroupedOption[] = [
    {
      options: ( selectedParticipants.length > 0 ? selectedParticipants : participantsOption ).map( participant => {
        const fallbackLabel = `${participant.object.firstname ?? ''} ${participant.object.lastname ?? ''}`.trim();
        return {
          id: participant.object.id,
          label: participant.label ?? fallbackLabel,
        };
      } ),
    },
  ];

  useEffect( () => {
    if ( !isFirstRender ) {
      getSectorsGroupedOption( sitesOption, selectedSites.map( site => { return site.object.id; } ) )
        .then( setSectorsOption );
      getThemesGroupedOption( sitesOption, selectedSites.map( site => { return site.object.id; } ) )
        .then( themes => {
          const mergedThemes = mergeThemeOptionsWithEventManagerThemes( themes, eventManagerActual );
          setThemesOption( mergedThemes );
        } );
    }
  }, [ eventManagerActual, selectedSites ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      setSelectedSectors( getSelectedSectors( sectorsOption, selectedSectors.map( sector => { return sector.object.id; } ) ) );
    }
  }, [ sectorsOption ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      getParticipantsOption( selectedSectors.map( sector => { return sector.object.id; } ) )
        .then( setParticipantsOption );
      getAdminsOption( selectedSectors.map( sector => { return sector.object.id; } ) )
        .then( setAdminsOption );
    }
  }, [ selectedSectors ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      if ( themesOption.length === 0 ) {
        return;
      }

      const selectedThemeIds = selectedThemes.length > 0
        ? selectedThemes.map( theme => { return theme.object.id; } )
        : eventManagerActual?.themes.map( theme => { return theme.id; } ) ?? [];

      getSelectedThemes( themesOption, selectedThemeIds )
        .then( setSelectedThemes );
    }
  }, [ eventManagerActual, themesOption ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      setSelectedParticipants( getSelectedParticipants( participantsOption, selectedParticipants.map( eventManager => { return eventManager.object.id; } ) ) );
    }
  }, [ participantsOption ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      setSelectedAdmins( getSelectedAdmins( adminsOption, selectedAdmins.map( eventManager => { return eventManager.object.id; } ) ) );
    }
  }, [ adminsOption ] );

  useEffect( () => {
    if ( !isFirstRender && initialOrderedParticipantIds.length === 0 && selectedParticipants.length > 0 ) {
      setInitialOrderedParticipantIds( [ ...selectedParticipants.map( participant => { return participant.object.id; } ) ] );
      const currentBacklogPosition = orderedParticipantIds.indexOf( DEFAULT_BACKLOG.ID );
      if ( initialBacklogPosition === -1 && currentBacklogPosition !== -1 ) {
        setInitialBacklogPosition( currentBacklogPosition );
      }
    }
  }, [ isFirstRender, selectedParticipants, orderedParticipantIds, initialOrderedParticipantIds, initialBacklogPosition ] );

  useEffect( () => {
    if ( isFirstRender ) { return; }

    const isFormRequirementMet = validateForm();
    const hasBasicChanges = detectBasicChanges();
    const hasSelectionChanges = detectSelectionChanges();
    const hasVisuChartsChanges = detectVisuChartsChanges();
    const hasParticipantOrderChanges = detectParticipantOrderChanges();
    const hasHolidayChanges = detectHolidayChanges();
    const hasChartSectionChanges = detectChartSectionChanges();
    const hasGraphicUnitChanges = detectGraphicUnitChanges();
    const hasPendingChartSettingsChanges = hasPendingChartSettings;

    const isChanged = !eventManagerActual
      || hasBasicChanges
      || hasSelectionChanges
      || hasVisuChartsChanges
      || hasParticipantOrderChanges
      || hasHolidayChanges
      || hasChartSectionChanges
      || hasGraphicUnitChanges
      || hasPendingChartSettingsChanges;

    const canSubmitWithChartOnly = eventManagerActual != null
      && ( hasChartSectionChanges || hasGraphicUnitChanges || hasPendingChartSettingsChanges );

    setIsFormValid( ( isFormRequirementMet && isChanged ) || canSubmitWithChartOnly );
    setIsFormChanged( isChanged );
  }, [
    isFirstRender,
    eventManagerName,
    backlogName,
    activeField,
    selectedSites,
    selectedSectors,
    selectedAdmins,
    selectedParticipants,
    orderedParticipantIds,
    initialOrderedParticipantIds,
    initialBacklogPosition,
    viewerIds,
    selectedThemes,
    navbarPosition,
    daysBeforeArchived,
    numberOfVisibleDays,
    visibleDays,
    createEventOnError,
    createEventOnWarning,
    safetyCrossThemeErrorId,
    safetyCrossThemeWarningId,
    selectedCountry,
    selectedState,
    selectedRegion,
    selectedHolidays,
    usePublicHolidays,
    removedClosedDays,
    addedClosedDays,
    chartSections,
    initialChartSections,
    graphicUnits,
    initialGraphicUnits,
    hasPendingChartSettings,
    eventManagerActual,
    visuChartsId,
    updatedVisuChartsId
  ] );

  const init = async () => {
    if ( clientId && siteId && sectorId ) {
      let eventManager: EventManager | null = null;
      if ( tableId ) {
        eventManager = await EventManagerService.getEventManagerById( tableId, token, dispatch );
        eventManager.participants = eventManager.participants.filter( item => { return item.id != DEFAULT_BACKLOG.ID; } );
        setEventManagerActual( eventManager );
        setEventManagerName( eventManager.name );
        setActiveField( Boolean( eventManager.activeField ) );
        setNavbarPosition( eventManager.navbarPosition );
        setDaysBeforeArchived( eventManager.eventAutoarchive.toString() );
        setNumberOfVisibleDays( eventManager.calendarItem.toString() );
        setVisibleDays( eventManager.daysToShow );
        setBacklogName( eventManager.backlogName );
        setCreateEventOnError( Boolean( eventManager.createEventOnError ) );
        setCreateEventOnWarning( Boolean( eventManager.createEventOnWarning ) );
        setSafetyCrossThemeErrorId( eventManager.safetyCrossThemeErrorId ?? null );
        setSafetyCrossThemeWarningId( eventManager.safetyCrossThemeWarningId ?? null );

        const visuCharts = await ChartOfSectionService.getVisualisationChartByEventManager( eventManager.id, token, dispatch );
        setVisuChartsId( visuCharts.map( visuChart => { return visuChart.id; } ) );
        setUpdatedVisuChartsId( visuCharts.map( visuChart => { return visuChart.id; } ) );

        const responseHoliday = await HolidayLocationService.getHolidayLocationByEventManager( eventManager.id, token, dispatch );
        setEventManagerHolidays( responseHoliday );

        const { orderedIdsWithBacklog, orderedIds, backlogPosition, participantsAsUmanforge } = getPositionInfos( eventManager );
        setOrderedParticipantIds( orderedIdsWithBacklog );
        setInitialOrderedParticipantIds( orderedIds );
        setInitialBacklogPosition( backlogPosition );
        setParticipantsList( participantsAsUmanforge );

        const updatedViewersId = getViewersId( eventManager.participants.map( participant => {return participant.id;} ), eventManager.observers.map( observer => {return observer.id;} ) );
        setViewerIds( updatedViewersId );

        const chartSectionsFromApi = await ChartSectionService.getByEventManager( eventManager.id, token, dispatch );
        const mappedChartSections: ChartSection[] = chartSectionsFromApi.map( section => {
          return {
            id: section.id,
            name: section.name,
          };
        } );
        setChartSections( mappedChartSections );
        setInitialChartSections( mappedChartSections );

        const chartUnitsFromApi = await ChartUnitService.getByEventManager( eventManager.id, token, dispatch );
        const mappedGraphicUnits = chartUnitsFromApi.map( unit => { return unit.value; } );
        setGraphicUnits( mappedGraphicUnits );
        setInitialGraphicUnits( mappedGraphicUnits );
        setInitialChartUnits( chartUnitsFromApi );
      }

      const sitesId: UUID[] = eventManager ? eventManager.site.map( site => { return site.id; } ) : [ siteId ];
      const updatedSitesOption = await getSitesOption( [ clientId ] );
      const updatedSelectedSites = getSelectedSites( updatedSitesOption, sitesId );
      setSitesOption( updatedSitesOption );
      setSelectedSites( updatedSelectedSites );

      const sectorsId: UUID[] = eventManager ? eventManager.sectors : [ sectorId ];
      const updatedSectorsOption = await getSectorsGroupedOption( updatedSitesOption, updatedSelectedSites.map( site => { return site.object.id; } ) );
      const updatedSelectedSectors = getSelectedSectors( updatedSectorsOption, sectorsId );
      setSectorsOption( updatedSectorsOption );
      setSelectedSectors( updatedSelectedSectors );

      const themesId: UUID[] = eventManager ? eventManager.themes.map( theme => { return theme.id; } ) : [];
      const updatedThemesOption = await getThemesGroupedOption( updatedSitesOption, updatedSelectedSites.map( site => { return site.object.id; } ) );
      const updatedSelectedThemes = await getSelectedThemes( updatedThemesOption, themesId );

      const fallbackTableThemes = eventManager ? CommonGlobalService.toOptions( eventManager.themes ) : [];
      const mergedThemesOption = [ ...updatedThemesOption ];
      fallbackTableThemes.forEach( fallbackTheme => {
        const alreadyExists = mergedThemesOption.some( themeOption => { return themeOption.object.id === fallbackTheme.object.id; } );
        if ( !alreadyExists ) {
          mergedThemesOption.push( fallbackTheme );
        }
      } );

      const finalSelectedThemes = updatedSelectedThemes.length > 0 ? updatedSelectedThemes : fallbackTableThemes;
      setThemesOption( mergedThemesOption );
      setSelectedThemes( finalSelectedThemes );

      const participantsId: UUID[] = eventManager ? eventManager.participants.filter( item => { return item.id != DEFAULT_BACKLOG.ID; } ).map( user => {return user.id;} ) : [];
      const updatedParticipantsOption = await getParticipantsOption( updatedSelectedSectors.map( site => { return site.object.id; } ) );
      const updatedSelectedParticipants = getSelectedParticipants( updatedParticipantsOption, participantsId );
      setParticipantsOption( updatedParticipantsOption );
      setSelectedParticipants( updatedSelectedParticipants );

      const adminsId: UUID[] = eventManager ? eventManager.admins.map( user => {return user.id;} ) : [];
      const updatedAdminsOption = await getAdminsOption( updatedSelectedSectors.map( site => { return site.object.id; } ) );
      const updatedSelectedAdmins = getSelectedAdmins( updatedAdminsOption, adminsId );
      setAdminsOption( updatedAdminsOption );
      setSelectedAdmins( updatedSelectedAdmins );
    }
  };

  const getSitesOption = async ( clientsId: UUID[] ): Promise<Option<Site>[]> => {
    const fetchedSites: Site[] = await CommonSiteService.getAllowedSitesByClientsId( clientsId, token, dispatch );
    const updatedSitesOption: Option<Site>[] = CommonGlobalService.toOptions( fetchedSites );
    return updatedSitesOption;
  };

  const getSectorsGroupedOption = async ( allSites: Option<Site>[], sitesId: UUID[] ): Promise<SectorGroupOption[]> => {
    const fetchedSectors: Sector[] = await CommonSectorService.getAllowedSectorsBySitesId( sitesId, token, dispatch );
    const groupedSectors: SectorGroupOption[] = [];
    for ( const [ siteIdOfSector, options ] of Object.entries( CommonGlobalService.groupBy( CommonGlobalService.toOptions( fetchedSectors ), option => { return option.object.siteId; } ) ) ) {
      const optionSectors = options ?? [];
      CommonUtilsService.sortByKey( optionSectors, 'label', 'asc' );
      groupedSectors.push( {
        label: allSites.find( site => { return site.object.id === siteIdOfSector; } )?.object.name ?? t( 'common:error.missing_site' ),
        options: optionSectors
      } );
    }
    return groupedSectors;
  };

  const getThemesGroupedOption = async ( allSites: Option<Site>[], sitesId: UUID[] ): Promise<Option<Theme>[]> => {
    const fetchedThemes: Theme[] = await ThemeService.getThemesBySites( sitesId, token, dispatch );
    const updatedThemesOption: Option<Theme>[] = CommonGlobalService.toOptions( fetchedThemes );
    return updatedThemesOption;
  };

  const getSelectedThemes = async ( allThemes: Option<Theme>[], themesId: UUID[] ): Promise<Option<Theme>[]> => {
    let updatedSelectedThemes = allThemes.filter( theme => { return themesId.includes( theme.object.id ); } );

    if ( tableId ) {
      updatedSelectedThemes = await Promise.all(
        updatedSelectedThemes.map( async theme => {
          theme.canDelete = await ThemeService.canDeleteThemeOnEventManager( theme.object.id, tableId, token, dispatch );
          return theme;
        } )
      );
    }

    return updatedSelectedThemes;
  };

  const getParticipantsOption = async ( sectorsId: UUID[] ): Promise<Option<UserUmanforge>[]> => {
    const fetchedUsers: UserUmanforge[] = await UsersService.getUsersOfSectors( sectorsId );
    const updatedUsersOption: Option<UserUmanforge>[] = CommonGlobalService.toOptions( fetchedUsers );
    return updatedUsersOption;
  };

  const getAdminsOption = async ( sectorsId: UUID[] ): Promise<Option<UserUmanforge>[]> => {
    const fetchedUsers: UserUmanforge[] = await UsersService.getUsersOfSectors( sectorsId );
    const updatedUsersOption: Option<UserUmanforge>[] = CommonGlobalService.toOptions( fetchedUsers );
    return updatedUsersOption;
  };

  const validateForm = (): boolean => {
    const hasParticipantObserverDuplicate = selectedParticipants.some( participant => { return viewerIds.includes( participant.object.id ); } );

    return (
      eventManagerName != null && eventManagerName !== '' &&
      backlogName != null && backlogName !== '' &&
      selectedSites.length > 0 &&
      selectedSectors.length > 0 &&
      selectedAdmins.length > 0 &&
      selectedParticipants.length > 0 &&
      Object.values( visibleDays ).some( selected => { return selected === true; } ) &&
      !hasParticipantObserverDuplicate
    );
  };

  const detectBasicChanges = (): boolean => {
    if ( !eventManagerActual ) {
      return true;
    }

    const isNameChange = eventManagerName !== eventManagerActual.name;
    const isBacklogNameChange = eventManagerActual.backlogName === DEFAULT_BACKLOG.NAME ?
      ( t( eventManagerActual.backlogName ) !== backlogName && eventManagerActual.backlogName !== backlogName )
      : eventManagerActual.backlogName !== backlogName;
    const isNavbarPositionChanged = navbarPosition !== eventManagerActual.navbarPosition;
    const isActiveFieldChanged = activeField !== Boolean( eventManagerActual.activeField );
    const isDaysBeforeArchivedChanged = daysBeforeArchived !== String( eventManagerActual.eventAutoarchive );
    const isNumberOfVisibleDaysChanged = numberOfVisibleDays !== String( eventManagerActual.calendarItem );
    const isCreateEventOnErrorChanged = createEventOnError !== Boolean( eventManagerActual.createEventOnError );
    const isCreateEventOnWarningChanged = createEventOnWarning !== Boolean( eventManagerActual.createEventOnWarning );
    const isSafetyCrossThemeErrorChanged = ( safetyCrossThemeErrorId ?? null ) !== ( eventManagerActual.safetyCrossThemeErrorId ?? null );
    const isSafetyCrossThemeWarningChanged = ( safetyCrossThemeWarningId ?? null ) !== ( eventManagerActual.safetyCrossThemeWarningId ?? null );

    return isNameChange || isActiveFieldChanged || isBacklogNameChange || isNavbarPositionChanged ||
           isDaysBeforeArchivedChanged || isNumberOfVisibleDaysChanged ||
           isCreateEventOnErrorChanged || isCreateEventOnWarningChanged ||
           isSafetyCrossThemeErrorChanged || isSafetyCrossThemeWarningChanged;
  };

  const detectSelectionChanges = (): boolean => {
    if ( !eventManagerActual ) {
      return true;
    }

    const isSelectedSiteIdsChanged = selectedSites.map( site => { return site.object.id; } ).sort().join() !== eventManagerActual.site.map( site => { return site.id; } ).sort().join();
    const isSelectedSectorIdsChanged = selectedSectors.map( sector => { return sector.object.id; } ).sort().join() !== eventManagerActual.sectors.sort().join();
    const isAdminIdsChanged = selectedAdmins.map( user => { return user.object.id; } ).sort().join() !== eventManagerActual.admins.map( user => { return user.id; } ).sort().join();
    const isParticipantIdsChanged = selectedParticipants.map( user => { return user.object.id; } ).sort().join() !== eventManagerActual.participants.map( user => { return user.id; } ).sort().join();
    const isViewerIdsChanged = viewerIds.sort().join() !== eventManagerActual.observers.map( user => { return user.id; } ).sort().join();
    const isThemeIdsChanged = selectedThemes.map( theme => { return theme.object.id; } ).sort().join() !== eventManagerActual.themes.map( theme => { return theme.id; } ).sort().join();
    const isVisibleDaysChanged = Object.values( visibleDays ).join() !== Object.values( eventManagerActual.daysToShow ).join();

    return isSelectedSiteIdsChanged || isSelectedSectorIdsChanged || isAdminIdsChanged ||
           isParticipantIdsChanged || isViewerIdsChanged || isThemeIdsChanged || isVisibleDaysChanged;
  };

  const detectParticipantOrderChanges = (): boolean => {
    const orderedParticipantIdsWithoutBacklog = orderedParticipantIds.filter( id => { return id !== DEFAULT_BACKLOG.ID; } );
    const currentBacklogPosition = orderedParticipantIds.indexOf( DEFAULT_BACKLOG.ID );

    const isParticipantOrderChanged = initialOrderedParticipantIds.length > 0 &&
      orderedParticipantIdsWithoutBacklog.join() !== initialOrderedParticipantIds.join();
    const isBacklogPositionChanged = initialBacklogPosition !== -1 && currentBacklogPosition !== initialBacklogPosition;

    return isParticipantOrderChanged || isBacklogPositionChanged;
  };

  const detectVisuChartsChanges = (): boolean => {
    return visuChartsId.sort().join() !== updatedVisuChartsId.sort().join();
  };

  const detectHolidayChanges = (): boolean => {
    const holidayInstance = new Holidays();
    holidayInstance.init( selectedCountry ?? undefined, selectedState ?? undefined, selectedRegion ?? undefined, { types: [ 'public' ] } );

    const allHolidays = holidayInstance.getHolidays( dayjs().year() )
      .map( holiday => { return holiday.rule; } )
      .sort();

    const userExcludedHolidays: string[] = ( eventManagerHolidays?.excludedRules ?? [] ).sort();
    const selectedExcludedHolidays: string[] = allHolidays
      .filter( selectedHoliday => { return !selectedHolidays.some( holiday => { return holiday.id === selectedHoliday; } ); } )
      .sort();

    const isHolidaysChange = (
      (
        usePublicHolidays && selectedCountry != null &&
        (
          ( selectedCountry !== '' && selectedCountry !== eventManagerHolidays?.country ) ||
          selectedState !== eventManagerHolidays?.state ||
          selectedRegion !== eventManagerHolidays?.region ||
          ( selectedExcludedHolidays.toString() !== userExcludedHolidays.toString() )
        )
      ) ||
      ( !usePublicHolidays && ( ( eventManagerHolidays != null ) !== usePublicHolidays ) )
    );
    const isClosedDaysChange = ( addedClosedDays.length > 0 || removedClosedDays.length > 0 );

    return isHolidaysChange || isClosedDaysChange;
  };

  const detectChartSectionChanges = (): boolean => {
    if ( !eventManagerActual ) {
      return chartSections.length > 0;
    }

    if ( chartSections.length !== initialChartSections.length ) {
      return true;
    }

    for ( let index = 0; index < chartSections.length; index++ ) {
      const current = chartSections[ index ];
      const initial = initialChartSections[ index ];
      if ( !initial ) {
        return true;
      }
      if ( current.id !== initial.id || current.name !== initial.name ) {
        return true;
      }
    }
    return false;
  };

  const detectGraphicUnitChanges = (): boolean => {
    if ( !eventManagerActual ) {
      return graphicUnits.length > 0;
    }

    if ( graphicUnits.length !== initialGraphicUnits.length ) {
      return true;
    }

    for ( let index = 0; index < graphicUnits.length; index++ ) {
      const current = graphicUnits[ index ];
      const initial = initialGraphicUnits[ index ];

      if ( current !== initial ) {
        return true;
      }
    }

    return false;
  };

  /*------------------------- Services -------------------------*/
  const tryManageVisualisationChartsByEventManager = async ( eventManagerId: UUID ): Promise<boolean> => {
    let isUpdate = false;

    const addedCharts = updatedVisuChartsId.filter( newItem => { return !visuChartsId.some( oldItem => { return newItem === oldItem; }  ); } );
    const deletedCharts = visuChartsId.filter( oldItem => { return !updatedVisuChartsId.some( newItem => { return newItem === oldItem; }  ); } );

    if ( addedCharts.length > 0 || deletedCharts.length > 0 ) {
      isUpdate = await ChartOfSectionService.manageVisualisationChartsByEventManager( eventManagerId, deletedCharts, addedCharts, token, dispatch );
    }

    return isUpdate;
  };

  const putHolidayLocation = async ( eventManagerId: UUID ): Promise<boolean> => {
    let isUpdate = false;
    if (
      usePublicHolidays == true && selectedCountry != null &&
      ( ( selectedCountry != '' && selectedCountry != eventManagerHolidays?.country ) || selectedState != eventManagerHolidays?.state || selectedRegion != eventManagerHolidays?.region )
    ) {
      const body: PutBodyHolidayLocation = {
        country: selectedCountry,
        state: selectedState ?? undefined,
        region: selectedRegion ?? undefined
      };
      isUpdate = await EventManagerService.putLocalisationOfHolidayToEventManager( eventManagerId, body, token, dispatch );
    }
    return isUpdate;
  };

  const deleteHolidayLocation = async ( eventManagerId: UUID ): Promise<boolean> => {
    let isDelete = false;
    if ( usePublicHolidays == false && ( ( eventManagerHolidays != null ) != usePublicHolidays ) ) {
      isDelete = await EventManagerService.deleteLocalisationOfHolidayToEventManager( eventManagerId, token, dispatch );
    }
    return isDelete;
  };

  const postAndDeleteHolidays = async ( eventManagerId: UUID ): Promise<boolean> => {
    let isAdded = false;
    let isRemoved = false;
    const holidayInstance = new Holidays();
    holidayInstance.init( selectedCountry ?? undefined, selectedState ?? undefined, selectedRegion ?? undefined, { types: [ 'public' ] } );
    const allHolidays = holidayInstance.getHolidays( dayjs().year() )
      .map( holiday => { return holiday.rule; } )
      .sort();
    const userExcludedHolidays: string[] = ( eventManagerHolidays?.excludedRules ?? [] )
      .sort();
    const selectedExcludedHolidays: string[] = allHolidays
      .filter( selectedHoliday => { return selectedHolidays.find( holiday => { return selectedHoliday == holiday.id; } ) == null; } )
      .sort();

    if ( usePublicHolidays == true && ( selectedExcludedHolidays.toString() !== userExcludedHolidays.toString() ) ) {
      const addededRules: string[] = selectedExcludedHolidays.filter( selectedRule => {
        return userExcludedHolidays.find( userRule => { return userRule === selectedRule; } ) == null;
      } );
      const removedRules: string[] = userExcludedHolidays.filter( userRule => {
        return selectedExcludedHolidays.find( selectedRule => { return selectedRule === userRule; } ) == null;
      } );

      if ( addededRules.length > 0 ) {
        isAdded = await EventManagerService.postExcludedHolidayToEventManager( eventManagerId, addededRules, token, dispatch );
      }
      if ( removedRules.length > 0 ) { isRemoved = await EventManagerService.deleteExcludedHolidayToEventManager( eventManagerId, removedRules, token, dispatch ); }
    }
    return isAdded || isRemoved;
  };

  const postClosedDays = async ( eventManagerId: UUID ): Promise<boolean> => {
    let isUpdate = false;
    if ( addedClosedDays.length > 0 ) {
      const body: PostClosedDayToEventManager[] = addedClosedDays.map( addedClosedDay => {
        return {
          startDate: dayjs( addedClosedDay.startDate ).format( FORMAT_DATE_API ),
          endDate: dayjs( addedClosedDay.endDate ).format( FORMAT_DATE_API )
        };
      } );
      isUpdate = await EventManagerService.postClosedDaysToEventManager( eventManagerId, body, token, dispatch );
    }
    return isUpdate;
  };

  const deleteClosedDays = async (): Promise<boolean> => {
    let isDelete = false;
    if ( removedClosedDays.length > 0 ) { isDelete = await EventManagerService.deleteClosedDaysToEventManager( removedClosedDays, token, dispatch ); }
    return isDelete;
  };

  const handleDelete = async () => {
    if ( tableId && onDeleteEventManager ) {
      const isDeleted = await onDeleteEventManager( tableId );
      if ( isDeleted ) {
        navigate( '/Home' );
      }
    }
  };

  const renderTabContent = () => {
    return (
      <div className="h-full py-3 px-4 bg-white rounded-xl border font-texte">
        <div className={`${activeTab === 'principal' ? 'flex flex-col gap-4' : 'hidden'}`}>
          <EventManagerMainSettings
            eventManagerName={ eventManagerName }
            setEventManagerName={ setEventManagerName }
            sitesOption={ sitesOption }
            selectedSites={ selectedSites }
            setSelectedSites={ setSelectedSites }
            sectorsOption={ sectorsOption }
            selectedSectors={ selectedSectors }
            setSelectedSectors={ setSelectedSectors }
            adminsOption={ adminsOption }
            selectedAdmins={ selectedAdmins }
            setSelectedAdmins={ setSelectedAdmins }
            participantsOption={ participantsOption }
            selectedParticipants={ selectedParticipants }
            handleParticipantIdsChange={ handleParticipantIdsChange }
            themesOption={ themesOption }
            selectedThemes={ selectedThemes }
            setSelectedThemes={ setSelectedThemes }
            navbarPosition={ navbarPosition }
            setNavbarPosition={ setNavbarPosition }
            numberOfVisibleDays={ numberOfVisibleDays }
            setNumberOfVisibleDays={ setNumberOfVisibleDays }
            daysBeforeArchived={ daysBeforeArchived }
            setDaysBeforeArchived={ setDaysBeforeArchived }
            visibleDays={ visibleDays }
            setVisibleDays={ setVisibleDays }
            activeField={ activeField }
            setActiveField={ setActiveField }
            backlogName={ backlogName }
            setBacklogName={ setBacklogName }
            usersCanCreateFromBoard={ usersCanCreateFromBoard }
            isAdminOfSelectedSectors={ isAdminOfSelectedSectors }
            isUserCreationView={ isUserCreationView }
            setIsUserCreationView={ setIsUserCreationView }
            newUserFirstName={ newUserFirstName }
            setNewUserFirstName={ setNewUserFirstName }
            newUserLastName={ newUserLastName }
            setNewUserLastName={ setNewUserLastName }
            newUserEmail={ newUserEmail }
            setNewUserEmail={ setNewUserEmail }
            onCreateUser={ handleCreateUser }
            isUserCreationFormValid={ isUserCreationFormValid() }
          />
        </div>
        <div className={`${activeTab === 'closure' ? 'flex flex-col gap-4' : 'hidden'}`}>
          <HolidayManager
            eventManager={ eventManagerActual }
            eventManagerHolidays={ eventManagerHolidays }
            usePublicHolidays={ usePublicHolidays }
            setUsePublicHolidays={ setUsePublicHolidays }
            addedClosedDays={ addedClosedDays }
            setAddedClosedDays={ setAddedClosedDays }
            removedClosedDays={ removedClosedDays }
            setRemovedClosedDays={ setRemovedClosedDays }
            selectedCountry={ selectedCountry }
            setSelectedCountry={ setSelectedCountry }
            selectedState={ selectedState }
            setSelectedState={ setSelectedState }
            selectedRegion={ selectedRegion }
            setSelectedRegion={ setSelectedRegion }
            selectedHolidays={ selectedHolidays }
            setSelectedHolidays={ setSelectedHolidays }
          />
        </div>
        <div className={`${activeTab === 'safety_cross' ? 'flex flex-col gap-4' : 'hidden'}`}>
          <EventManagerSafetyTab
            createEventOnWarning={ createEventOnWarning }
            setCreateEventOnWarning={ setCreateEventOnWarning }
            createEventOnError={ createEventOnError }
            setCreateEventOnError={ setCreateEventOnError }
            safetyCrossThemeWarningId={ safetyCrossThemeWarningId }
            setSafetyCrossThemeWarningId={ setSafetyCrossThemeWarningId }
            safetyCrossThemeErrorId={ safetyCrossThemeErrorId }
            setSafetyCrossThemeErrorId={ setSafetyCrossThemeErrorId }
            chartSections={ chartSections }
            setChartSections={ setChartSections }
            defaultChartSettingManagerId={ selectedParticipants[ 0 ]?.object.id ?? participantsList[ 0 ]?.id ?? '' }
            defaultChartSettingUnitId={ initialChartUnits[ 0 ]?.id ?? null }
            chartSectionListRef={ chartSectionListRef }
            onPendingChartSettingsChange={ setHasPendingChartSettings }
            graphicUnitSuffix={ graphicUnitSuffix }
            setGraphicUnitSuffix={ setGraphicUnitSuffix }
            graphicUnits={ graphicUnits }
            setGraphicUnits={ setGraphicUnits }
            chartUnitOptions={ chartUnitOptions }
            managerOptions={ managerOptions }
            eventManagerThemes={ selectedThemes.map( theme => { return theme.object; } ) }
            eventManagerId={eventManagerActual?.id || ''}
          />
        </div>
        <div className={`${activeTab === 'participant_order' ? 'flex flex-col gap-4' : 'hidden'}`}>
          <EventManagerParticipantOrderTab
            participantsList={ participantsList }
            orderedParticipantIds={ orderedParticipantIds }
            setOrderedParticipantIds={ setOrderedParticipantIds }
            tableId={ tableId }
            backlogName={ backlogName }
            triggerParticipantUpdate={ triggerParticipantUpdate }
            setTriggerParticipantUpdate={ setTriggerParticipantUpdate }
          />
        </div>
        {
          tableId && (
            <div className={`${ activeTab === 'global_visualisation' ? 'flex flex-col gap-4' : 'hidden'}`}>
              <EventManagerVisualisationSetting
                eventManagerId={ tableId }
                visuChartsId={ updatedVisuChartsId }
                setVisuChartsId={ setUpdatedVisuChartsId }
              />
            </div>
          )
        }
      </div>
    );
  };

  //--------------- Functions ---------------\\
  const updateGlobalSettings = async () => {
    if ( clientId ) {
      const settingsResponse: GlobalSetting[] = await GlobalSettingsService.getGlobalSettings( clientId, token, dispatch );
      const visibleDaySetting = settingsResponse.find( setting => { return setting.keyframe == KEYFRAME.DAYS_DISPLAY; } );
      if ( visibleDaySetting ) {
        setVisibleDays( CommonGlobalService.getDaysFromBinary( Number( visibleDaySetting.value ) ) );
      }
      const numberOfVisibleDaysSetting = settingsResponse.find( setting => { return setting.keyframe == KEYFRAME.CALENDAR_COLUMN; } );
      if ( numberOfVisibleDaysSetting ) {
        setNumberOfVisibleDays( String( numberOfVisibleDaysSetting.value ) );
      }
      const usersCanCreateSetting = settingsResponse.find( setting => { return setting.keyframe == KEYFRAME.CAN_CREATE_FROM_BOARD; } );
      if ( usersCanCreateSetting ) {
        setUsersCanCreateFromBoard( usersCanCreateSetting.value === '1' );
      }
    }
  };

  const handleParticipantIdsChange = ( newParticipants: Option<UserUmanforge>[] ) => {
    const addedParticipants = newParticipants
      .filter( newParticipant => {
        return !selectedParticipants.some( participant => { return participant.object.id === newParticipant.object.id; } );
      } )
      .map( user => { return user.object.id; } );
    const removedParticipants = selectedParticipants
      .filter( participant => {
        return !newParticipants.some( newParticipant => { return participant.object.id === newParticipant.object.id; } );
      } )
      .map( user => { return user.object.id; } );

    setSelectedParticipants( newParticipants );
    setOrderedParticipantIds( prevOrdered => {
      let newOrdered = [ ...prevOrdered ];
      if ( addedParticipants.length > 0 ) {
        newOrdered = [ ...newOrdered, ...addedParticipants ];
      }
      if ( removedParticipants.length > 0 ) {
        newOrdered = newOrdered.filter( id => { return !removedParticipants.includes( id ); } );
      }
      return newOrdered;
    } );

    if ( addedParticipants.length > 0 ) {
      UsersService.getUsersOfSectors( selectedSectors.map( user => { return user.object.id; } ) )
        .then( users => {
          const relevantUsers = users.filter( user => { return addedParticipants.includes( user.id ); } );
          const updatedList = [
            ...participantsList.filter( user => { return !removedParticipants.includes( user.id ); } ),
            ...relevantUsers
          ];
          setParticipantsList( updatedList );
        } )
        .catch( error => { console.error( 'Error fetching participants:', error ); } );
    } else if ( removedParticipants.length > 0 ) {
      const filteredList = participantsList.filter( user => { return !removedParticipants.includes( user.id ); } );
      setParticipantsList( filteredList );
    }

    const updatedViewerIds = viewerIds.filter( viewerId => {
      return !newParticipants.some( newParticipant => { return newParticipant.object.id === viewerId; } );
    } );
    if ( updatedViewerIds.length !== viewerIds.length ) {
      setViewerIds( updatedViewerIds );
    }
  };

  const isValidEmail = ( email: string ): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test( email );
  };

  const isUserCreationFormValid = (): boolean => {
    return (
      newUserFirstName.trim() !== '' &&
      newUserLastName.trim() !== '' &&
      newUserEmail.trim() !== '' &&
      isValidEmail( newUserEmail )
    );
  };

  const handleCreateUser = async () => {
    try {
      const body: NewUserPayload = {
        firstname: newUserFirstName,
        lastname: newUserLastName,
        email: newUserEmail,
        clients: clientId ? [ { id: clientId, isAdmin: false } ] : [],
        sites: selectedSites.map( s => { return { id: s.object.id, isAdmin: false }; } ),
        sectors: selectedSectors.map( s => { return { id: s.object.id, isAdmin: false }; } ),
        language: undefined,
        timezone: undefined,
      };

      const response = await UsersService.postUser( body, token, dispatch );
      if ( response && response.id ) {
        const users = await UsersService.getUsersOfSectors( selectedSectors.map( sec => { return sec.object.id; } ) );
        const options = CommonGlobalService.toOptions( users );

        setParticipantsOption( options );

        setAdminsOption( prevAdmins => {
          const existing = prevAdmins ?? [];
          const newOnes = options.filter( o => {return !existing.some( e => {return e.object.id === o.object.id;} );} );
          return [ ...existing, ...newOnes ];
        } );

        const createdOption = options.find( o => { return o.object.id === response.id; } );
        if ( createdOption ) {
          setSelectedParticipants( prev => {
            if ( prev.some( p => {return p.object.id === createdOption.object.id;} ) ) {return prev;}
            return [ ...prev, createdOption ];
          } );
          setOrderedParticipantIds( prev => {
            if ( prev.includes( response.id ) ) {return prev;}
            return [ ...prev, response.id ];
          } );
        }
        setNewUserFirstName( '' );
        setNewUserLastName( '' );
        setNewUserEmail( '' );
      }
    } catch ( err ) {
      console.error( 'Error creating user:', err );
    } finally {
      setIsUserCreationView( false );
    }
  };

  //--------------- Events ---------------\\
  const handleSubmit = async () => {
    setTriggerParticipantUpdate( true );
    const submitPendingFromSnapshot = chartSectionListRef.current?.submitPending;

    const participantIdsWithoutBacklog = orderedParticipantIds.map( ( orderedParticipant, index ) => { return { id: orderedParticipant, position: index };  } ).filter( participant => { return participant.id !== DEFAULT_BACKLOG.ID; } );
    const participantsToSubmit = orderedParticipantIds.length > 0 ? participantIdsWithoutBacklog : selectedParticipants.map( ( orderedParticipant, index ) => { return { id: orderedParticipant.object.id, position: index };  } );

    if ( tableId && orderedParticipantIds.length > 0 ) {
      const backlogPosition = orderedParticipantIds.indexOf( DEFAULT_BACKLOG.ID );
      if ( backlogPosition !== -1 ) {
        setBacklogPositionForTable( backlogPosition.toString() );
      }
    }

    const base: TableCreation = {
      name: eventManagerName.trim(),
      backlogName: backlogName.trim(),
      activeField: activeField,
      calendarItem: Number( numberOfVisibleDays ),
      navbarPosition,
      eventAutoarchive: Number( daysBeforeArchived ),
      sectorIds: selectedSectors.map( sector => { return sector.object.id; } ),
      participants: participantsToSubmit,
      adminIds: selectedAdmins.map( user => { return user.object.id; } ),
      observersIds: viewerIds,
      themeIds: selectedThemes.map( theme => { return theme.object.id; } ),
      daysToShow: visibleDays,
      safetyCrossThemeErrorId,
      safetyCrossThemeWarningId,
      createEventOnError,
      createEventOnWarning
    };

    if ( isFormValid ) {
      const params: TableCreation = base;
      const eventManagerId = await onSubmit( params );
      const resolvedEventManagerId = eventManagerId ?? tableId;

      if ( resolvedEventManagerId ) {

        await tryManageVisualisationChartsByEventManager( resolvedEventManagerId );

        if ( usePublicHolidays ) {
          await putHolidayLocation( resolvedEventManagerId );
          await postAndDeleteHolidays( resolvedEventManagerId );
        } else {
          await deleteHolidayLocation( resolvedEventManagerId );
        }
        await deleteClosedDays();
        await postClosedDays( resolvedEventManagerId );

        let chartSectionIdMap = new Map<string, UUID>();

        if ( detectChartSectionChanges() ) {
          const chartSectionSaveResult = await saveChartSections( resolvedEventManagerId, chartSections, initialChartSections, token, dispatch );
          chartSectionIdMap = chartSectionSaveResult.idMap;
        }
        if ( submitPendingFromSnapshot ) {
          await submitPendingFromSnapshot( chartSectionIdMap );
        }

        if ( detectGraphicUnitChanges() ) {
          await saveGraphicUnits( resolvedEventManagerId, graphicUnits, initialGraphicUnits, initialChartUnits, token, dispatch );
        }
      }

      onClose();
    }
  };

  //--------------- Front ---------------\\
  return (
    <BaseModal
      isOpen={true}
      onClose={onClose}
      noConfirmationModal={!isFormChanged}
      icon={
        tableId ?
          <EditBoxIcon className="bg-orange-200 p-2 rounded-md w-10 h-10" />
          :
          <PlusIcon className="bg-green-200 p-2 rounded-md w-10 h-10" />
      }
      footer={
        <BottomModal
          tableId={ tableId }
          formValid={ isFormValid }
          onCreate={ handleSubmit }
          onUpdate={ handleSubmit }
          onDelete={ () => { return setIsDeleteModalOpen( true ); } }
        />
      }
      title={tableId ? t( 'translation:modification_of_an_event_manager' ) : t( 'translation:creation_of_an_event_manager' )}
    >

      <div className="flex flex-col pb-4 px-7 gap-2">
        <div className='h-fit'>
          <Tabs items={pages} activeItem={pages.findIndex( page => { return page.id === activeTab; } )} onItemClick={ index => { setActiveTab( pages[ index ].id ); }} />
        </div>
        { renderTabContent() }
      </div>

      <ConfirmationModal
        isOpen={ isDeleteModalOpen }
        onConfirm={ handleDelete }
        onCancel={ () => { return setIsDeleteModalOpen( false ); } }
        title={ t( 'translation:delete_event_manager' ) }
        description={ t( 'translation:are_you_sure_that_you_wanna_delete_the_event_manager_it_is_irreversible' ) }
      />
    </BaseModal>
  );
};

function EventManagerModal( props: EventManagerModalProps ): React.ReactElement {
  return <EventManagerModalInner {...props} />;
}

export default EventManagerModal;
